import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Fade } from '../../animation/animation';
import { useMediaQuery } from 'react-responsive';
import styles from './initial-view.module.scss';
import BackgroundImage from '../background-image/background-image';
import { isMobile } from 'react-device-detect';

import Description from './description/descripiton';
import Visits from '../visits/visits';
import Button from '../button/button';
import CallbackButton from '../callback-button/callback-button';
import FlyingBtn from '../flying-btn/flying-btn';
import { State } from '../../reducers/history/interfaces/state.interface';
import { StateType } from '../../reducers/history/enums/state-type.enum';
import { AnimConfig } from '../../models/anim-config.model';
import { Service } from '../../models/service.model';
import { useDispatch, useSelector } from 'react-redux';
import { displayPopupWindow, toggleFlyingButton, updateCurrentService, setActivePopupWindowId, setSignUpOpened, setQuizStarted } from '../../reducers/quiz/quiz.actions';
import { emitEvent } from '../../event-emitter';
import { EVENT_START_BY_FLYING_BTN, EVENT_NEXT_BY_SCROLL, EVENT_START_BY_TIMER, EVENT_OPEN_SIGN_UP_BY_TIMER, EVENT_START_BY_MAIN_BUTTON, EVENT_START_QUIZ_COMMON } from '../../constants';
import { scrollToTop } from '../../utils/library';
import { ecommerce, EcommerceAction } from '../../ecommerce.module';
import { getServiceName } from '../../utils/library';
import { ConvrTestVariation } from '../../interfaces/convr-test-variation.interface';
import { ConvrTestName } from '../../enums/convr-test-name.enum';
import { InstructionPopup } from '../instruction-popup/instruction-popup';
import { RootState } from '../../store';
import { PopupWindowID } from '../../enums/popup-window-id.enum';
import { PhoneFormData } from '../../interfaces/phone-form-data.interface';
import { WhatsappFormDefault } from '../whatsApp-form/whatsapp-form-default';
import { FormType } from '../../enums/form-type.enum';
import { useIntersection } from '../../hooks/use-intersection.hook';
import { PDNNotification } from '../pdn-notification/pdn-notification';
import { usePolicyAgree } from '../../hooks/use-policy-agree.hook';

interface InitialViewProps {
	currentService: Service | 'selection'
	serviceList: Service[]
	next: (state: State, callback?: Function) => void
	animation: AnimConfig
	flyingButton?: boolean
	whatsApp?: {
		enable: boolean;
		phone: string;
		start_page_phone?: string;
		remove_lead_id_start_page?: boolean;
		onFirstScreen?: boolean;
		start_page_text?: string;
	}
	flyingButtonStatus: boolean;
	flyingButtonEnabled: boolean;
	additionalCallbackButton?: {
		value: string
		link: string
	}
	children?: React.ReactNode,
	hasHeader?: boolean,
	description: string
	canStartOnScroll?: boolean
	wasOpened?: boolean
	initialPageIndex?: number;
	testVariations?: ConvrTestVariation[];
	buttonType?: {
		form: string;
		size: string;
		animation: string;
	};
	backgroundImage?: string;
	phoneFormState: PhoneFormData;
	autoAction?: {
		action: "start_quiz" | "open_sign_up",
		timer: number //seconds
	}
}

const getStartPageProduct = (currentService: Service | "selection") => ({
	id: 'initial-view',
	name: 'Начальная страница',
	category: getServiceName(currentService)
});

export default function InitialView(props: InitialViewProps) {
	const {
		animation,
		currentService,
		serviceList,
		flyingButtonStatus,
		flyingButtonEnabled,
		additionalCallbackButton,
		children,
		hasHeader = true,
		description,
		canStartOnScroll,
		wasOpened,
		initialPageIndex,
		whatsApp,
		testVariations,
		buttonType,
		backgroundImage,
		phoneFormState,
		autoAction
	} = props;
	const fromMobile: boolean = useMediaQuery({ maxWidth: 900 });
	const dispatch = useDispatch();
	const nextButton: React.MutableRefObject<Button | null> = useRef(null);

	const [buttonText, setButtonText] = useState("Рассчитать цену онлайн");
	const [descriptionText, setDescriptionText] = useState(description);
	const [minorButtonExist] = useState(true);
	const [visitsMultiplier, setVisitsMultiplier] = useState(1);
	const { activePopupWindowID, popupWindow } = useSelector((state: RootState) => state.quiz);
	const lead_id = useSelector((state: RootState) => state.client?.lead?._id);

	const { started, signUpOpened } = useSelector((state: RootState) => state.quiz);
	const autoActionTimer = useRef<NodeJS.Timeout | null>(null);

	const goTo = useCallback((state: State) => {
		props.next(state, () => {
			scrollToTop();
			ecommerce.add(EcommerceAction.CART, getStartPageProduct(currentService));
		})
	}, [currentService, props.next]);

	const startQuiz = useCallback((trigger?: string) => {
		goTo({ type: serviceList.length > 1 ? StateType.SELECTION : StateType.QUESTIONS, data: { order: 1 } });
		emitEvent(EVENT_START_QUIZ_COMMON);
		if (trigger == 'flying-btn') emitEvent(EVENT_START_BY_FLYING_BTN);
		if (trigger == 'timer') emitEvent(EVENT_START_BY_TIMER);
		if (trigger == 'main_button') emitEvent(EVENT_START_BY_MAIN_BUTTON);
		if (trigger == 'scroll') emitEvent(EVENT_NEXT_BY_SCROLL);
		dispatch(setQuizStarted(true));
	}, [goTo, dispatch, serviceList]);

	const startByScrollHandler = useCallback(() => {
		startQuiz("scroll");
		return true;
	}, [startQuiz]);

	const { policyStatus, policyAgreementMode } = usePolicyAgree();

	const enableStartOnScroll = useMemo(() => {
		return (Boolean(canStartOnScroll && (policyAgreementMode !== "hard" || policyStatus)))
	}, [canStartOnScroll, policyAgreementMode, policyStatus]);

	const { intersectionObserverRef } = useIntersection(startByScrollHandler, enableStartOnScroll);

	useEffect(() => {
		if (autoAction && (!started && !signUpOpened)) {
			autoActionTimer.current = setTimeout(() => {
				if (autoAction.action == "open_sign_up") openSignUp("timer");
				if (autoAction.action == "start_quiz") startQuiz("timer");
			}, autoAction.timer * 1000);
		}
		return () => {
			autoActionTimer.current && clearTimeout(autoActionTimer.current);
		}
	}, [autoAction, started, signUpOpened]);

	useEffect(() => {
		if (testVariations?.length) {
			const buttonTextPart1FromTest = testVariations.find(variation => variation.name == ConvrTestName.MAIN_BUTTON_PART_1)?.variant?.payload;
			const buttonTextPart2FromTest = testVariations.find(variation => variation.name == ConvrTestName.MAIN_BUTTON_PART_2)?.variant?.payload;
			const descriptionTextFromTest = testVariations.find(variation => variation.name == ConvrTestName.MAIN_DESCRIPTION)?.variant?.payload;
			const visitsMultiplierFromTest = testVariations.find(variation => variation.name == ConvrTestName.VISITS_COUNT)?.variant?.payload;

			if (buttonTextPart1FromTest !== undefined && buttonTextPart2FromTest !== undefined) {
				setButtonText(buttonTextPart1FromTest + " " + buttonTextPart2FromTest);
			}

			if (descriptionTextFromTest !== undefined) setDescriptionText(descriptionTextFromTest);
			if (visitsMultiplierFromTest !== undefined) setVisitsMultiplier(visitsMultiplierFromTest);
		}
	}, [testVariations]);

	useEffect(() => {
		if (wasOpened && initialPageIndex === 0) {
			ecommerce.add(EcommerceAction.REVIEW, getStartPageProduct(currentService));
		}
	}, [currentService, wasOpened, initialPageIndex]);

	useEffect(() => {
		if (serviceList.length == 1 && currentService == 'selection') dispatch(updateCurrentService(serviceList[0]));
	}, [dispatch, currentService, serviceList]);


	const openSignUp = useCallback((trigger?: string) => {
		goTo({ type: StateType.SIGN_UP });
		dispatch(setSignUpOpened(true));
		if (trigger == 'timer') emitEvent(EVENT_OPEN_SIGN_UP_BY_TIMER);
	}, [goTo, dispatch]);

	const submitWhatsappForm = useCallback(async () => {
		if (!lead_id) {
			setTimeout(() => {
				dispatch(setActivePopupWindowId(PopupWindowID.WHATSAPP_INSTRUCTIONS));
				dispatch(displayPopupWindow(true));
			}, 1000);
		} else {
			setTimeout(() => {
				dispatch(displayPopupWindow(false));
				openSignUp()
			}, 1000);
		}
	}, [lead_id]);

	const onClosePopup = useCallback(() => {
		dispatch(displayPopupWindow(false));
		setTimeout(() => {
			openSignUp();
		}, 300);
	}, [dispatch, openSignUp]);

	return (<>
		{(activePopupWindowID == PopupWindowID.WHATSAPP_INSTRUCTIONS && whatsApp) &&
			<InstructionPopup
				whatsappParams={{
					phone: whatsApp.start_page_phone || whatsApp.phone,
					text: whatsApp.start_page_text,
					form_type: FormType.START_PAGE_WHATSAPP,
					remove_lead_id: whatsApp.remove_lead_id_start_page
				}}
				onClose={onClosePopup}
				onButtonClick={submitWhatsappForm}
				active={popupWindow} />
		}
		<section className={hasHeader ? styles.default : styles.withoutHeader}>
			{flyingButtonEnabled && <FlyingBtn
				onClick={() => startQuiz('flying-btn')}
				active={animation.active && flyingButtonStatus}
			/>}

			<BackgroundImage service={currentService} active={animation.active} image={backgroundImage} />

			{children}

			{!fromMobile &&
				<Fade up delay={50} when={animation.active}>
					<Description text={descriptionText} />
				</Fade>
			}

			<Fade left when={animation.active}>
				<div
					style={{ display: 'inline-block' }}
					onMouseEnter={() => dispatch(toggleFlyingButton(false))}
					onMouseLeave={() => dispatch(toggleFlyingButton(true))}
				>
					<Button
						ref={nextButton}
						id="main_start_button"
						onClick={() => startQuiz('main_button')}
						mode='initial-view'
						value={buttonText}
						modifications={buttonType}
					/>
					{(isMobile && whatsApp?.enable && whatsApp.onFirstScreen) &&
						<WhatsappFormDefault
							whatsappParams={{
								phone: whatsApp.start_page_phone || whatsApp.phone,
								text: whatsApp.start_page_text,
								form_type: FormType.START_PAGE_WHATSAPP,
								remove_lead_id: whatsApp.remove_lead_id_start_page
							}}
							buttonMode="whatsAppInitial"
							buttonText='Узнать по whatsapp'
							showAgreement={false}
							showNoWhatsappButton={false}
							agreementValue={phoneFormState.agreement}
							onSubmit={submitWhatsappForm}
						/>
					}
					{minorButtonExist && <CallbackButton additionalCallbackButton={additionalCallbackButton} onClick={() => openSignUp()} />}
				</div>

				{fromMobile && <Description text={descriptionText} />}
				{Boolean(visitsMultiplier) && <Visits multiplier={visitsMultiplier} />}
			</Fade>
		</section>
		<div className={styles.intersectionObserver} ref={intersectionObserverRef}></div>
	</>);
}